import { useState, useMemo } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  IconButton,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { usePanelStyles } from '@/styles/utilities/hooks';
import { getFMOInputColumns, getFMOInputDefaultVisibility } from '../config/fmoInputFields.jsx';

export default function FMOInputsPanel({ 
  fmoInputs,  // Now receives FILTERED FMO inputs
  loading, 
  error,
  selectedFMOInput,
  onFMOInputSelect,
  zipfileName,
  onClose,
  isBottomPanel
}) {
  const theme = useTheme();
  const panelStyles = usePanelStyles();
  
  // Use local FMO input column definitions
  const columns = useMemo(() => {
    return getFMOInputColumns(theme, {
      selectedFMOInput,
      onFMOInputSelect
    });
  }, [theme, selectedFMOInput, onFMOInputSelect]);
  
  // Column visibility state
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => 
    getFMOInputDefaultVisibility(columns)
  );
  
  // Use actual count of filtered FMO inputs
  const displayCount = fmoInputs?.length || 0;

  return (
    <Paper sx={{ 
      height: isBottomPanel ? '100%' : 'auto',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 0,
      backgroundColor: 'background.paper'
    }}>
      {/* Header */}
      <Box sx={{
        ...panelStyles.header,
        bgcolor: 'grey.50',
        borderTop: isBottomPanel ? 'none' : '1px solid',
        borderBottom: '1px solid',
        borderTopColor: 'divider',
        borderBottomColor: 'divider'
      }}>
        <Typography sx={panelStyles.headerTitle}>
          FMO Inputs: {zipfileName} ({displayCount})
        </Typography>
        <IconButton 
          size="small" 
          onClick={onClose}
          sx={panelStyles.headerIconButton}
        >
          <CloseIcon sx={{ fontSize: theme.dimensions.icon.small }} />
        </IconButton>
      </Box>
      
      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {error && (
          <Alert severity="error" sx={{ mb: theme.spacing(2) }}>
            {error}
          </Alert>
        )}
        
        <DataGridPro
          rows={fmoInputs}  // Pass FILTERED FMO inputs
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          getRowId={(row) => row.id_}
          loading={loading}
          // Removed onRowClick to allow proper deselection
          onRowSelectionModelChange={(model) => {
            // Handle selection change including deselection
            const selectedId = Array.from(model.ids)[0];
            if (selectedId) {
              const fmoInput = fmoInputs.find(e => e.id_ === selectedId);
              if (fmoInput) onFMOInputSelect(fmoInput);
            } else {
              // User deselected - clear selection and close viewer
              onFMOInputSelect(null);
            }
          }}
          hideFooter
          rowHeight={theme.dimensions.dataGrid.rowHeight}
          columnHeaderHeight={theme.dimensions.dataGrid.headerHeight}
          disableMultipleRowSelection
          rowSelectionModel={
            selectedFMOInput 
              ? { ids: new Set([selectedFMOInput.id_]), type: 'include' }
              : { ids: new Set(), type: 'include' }
          }
          sx={{
            height: '100%',
            border: 'none',
            '& .MuiDataGrid-cell': {
              fontSize: theme.typography.fontSize.sm,
              padding: theme.spacing(0, 1),
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              '&.Mui-selected': {
                backgroundColor: theme.palette.selection.light,
                '&:hover': {
                  backgroundColor: theme.palette.selection.main,
                }
              }
            }
          }}
          slots={{
            loadingOverlay: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                height: '100%'
              }}>
                <CircularProgress size={theme.spacing(4)} />
              </Box>
            )
          }}
        />
      </Box>
    </Paper>
  );
}